import { useState, useCallback, useEffect } from 'react';
import { Check, Droplets, FlaskConical, AlertCircle } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { Burette, ErlenmeyerFlask } from '@/components/lab';

const stepInfoContent: Record<number, { title: string; content: string; formulas?: string[] }> = {
  1: {
    title: 'Prepare for Titration',
    content: 'Record the initial burette reading and prepare to swirl the flask gently during titration. Place the flask on a white tile or paper for better color observation.',
    formulas: ['Record initial burette reading', 'Place flask on white tile']
  },
  2: {
    title: 'Beginning the Titration',
    content: 'Start by adding HCl at a moderate rate. The antacid is in excess, so initial additions react quickly. Watch the indicator color - it should remain yellow (basic) during the early stages.',
    formulas: ['Antacid + HCl → Salt + H₂O', 'CaCO₃ + 2HCl → CaCl₂ + H₂O + CO₂']
  },
  3: {
    title: 'Approaching the Endpoint',
    content: 'As the antacid is consumed, the color begins changing from yellow toward orange. The color change may appear locally where drops enter. Slow down and add dropwise when you see the first hints of orange.',
    formulas: ['Near endpoint: add 0.1-0.2 mL at a time', 'Wait for color to distribute']
  },
  4: {
    title: 'Detecting the Endpoint',
    content: 'The endpoint is reached when the solution turns a persistent pink colour that does not fade with swirling. Stop adding HCl immediately when this pink colour persists.',
    formulas: ['Endpoint: pH ~ 3.7 (methyl orange transition)', 'Color: persistent pink']
  },
  5: {
    title: 'Recording the Final Volume',
    content: 'Wait for the meniscus to settle, then read the final volume at eye level. Record to 0.01 mL precision. The volume of HCl used equals the final reading minus the initial reading.',
    formulas: ['V_HCl = V_final - V_initial', 'mEq HCl = M x V (mL)']
  }
};

// Methyl Orange indicator colors for titration
const INDICATOR_COLORS = {
  basic: '#FFD700',      // Yellow (pH > 4.4)
  transition1: '#FFA500', // Yellow-Orange
  transition2: '#FF8C00', // Orange
  transition3: '#FF6347', // Red-Orange
  endpoint: '#FF4500'     // Red (pH < 3.1)
};

export default function Part4Titration() {
  const [stirrerOn, setStirrerOn] = useState(false);
  const [titrationStarted, setTitrationStarted] = useState(false);
  const [approachingEndpoint, setApproachingEndpoint] = useState(false);
  const [endpointReached, setEndpointReached] = useState(false);
  const [finalRecorded, setFinalRecorded] = useState(false);

  const [buretteReading, setBuretteReading] = useState(0.75);
  const [initialReading] = useState(0.75);
  const [finalReading, setFinalReading] = useState(0);
  const [solutionColor, setSolutionColor] = useState(INDICATOR_COLORS.basic);
  const [currentStep, setCurrentStep] = useState(1);
  const [stopcockOpen, setStopcockOpen] = useState(false);
  const [stirSpeed, setStirSpeed] = useState(0);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  // Target volume for endpoint (simulating ~10 mEq antacid)
  const targetVolume = 18.5 + Math.random() * 3;

  const completedSteps = [stirrerOn, titrationStarted, approachingEndpoint, endpointReached, finalRecorded].filter(Boolean).length;

  // Titration simulation with color changes
  useEffect(() => {
    if (titrationStarted && stopcockOpen && !endpointReached) {
      const interval = setInterval(() => {
        setBuretteReading(prev => {
          const increment = approachingEndpoint ? 0.05 : 0.2;
          const newReading = prev + increment;

          const volumeUsed = newReading - initialReading;
          const progress = volumeUsed / targetVolume;

          // Color transitions based on progress
          if (progress >= 1) {
            setEndpointReached(true);
            setFinalReading(parseFloat(newReading.toFixed(2)));
            setSolutionColor(INDICATOR_COLORS.endpoint);
            setStopcockOpen(false);
            setCurrentStep(5);
            return newReading;
          } else if (progress > 0.95) {
            setSolutionColor(INDICATOR_COLORS.transition3);
            if (!approachingEndpoint) {
              setApproachingEndpoint(true);
              setCurrentStep(4);
            }
          } else if (progress > 0.85) {
            setSolutionColor(INDICATOR_COLORS.transition2);
            if (!approachingEndpoint) {
              setApproachingEndpoint(true);
              setCurrentStep(4);
            }
          } else if (progress > 0.7) {
            setSolutionColor(INDICATOR_COLORS.transition1);
          }

          return newReading;
        });
      }, approachingEndpoint ? 300 : 150);

      return () => clearInterval(interval);
    }
  }, [titrationStarted, stopcockOpen, endpointReached, approachingEndpoint, initialReading, targetVolume]);

  const handleStartStirrer = useCallback(() => {
    if (!stirrerOn) {
      setStirrerOn(true);
      setStirSpeed(5);
      setCurrentStep(2);
    }
  }, [stirrerOn]);

  const handleBeginTitration = useCallback(() => {
    if (stirrerOn && !titrationStarted) {
      setTitrationStarted(true);
      setCurrentStep(3);
    }
  }, [stirrerOn, titrationStarted]);

  const handleToggleStopcock = useCallback(() => {
    if (titrationStarted && !endpointReached) {
      setStopcockOpen(prev => !prev);
    }
  }, [titrationStarted, endpointReached]);

  const handleRecordFinal = useCallback(() => {
    if (endpointReached && !finalRecorded) {
      setFinalRecorded(true);
    }
  }, [endpointReached, finalRecorded]);

  const volumeUsed = finalReading - initialReading;

  // Get color name for display
  const getColorName = () => {
    if (solutionColor === INDICATOR_COLORS.basic) return 'Yellow (Basic)';
    if (solutionColor === INDICATOR_COLORS.transition1) return 'Yellow-Orange';
    if (solutionColor === INDICATOR_COLORS.transition2) return 'Orange';
    if (solutionColor === INDICATOR_COLORS.transition3) return 'Red-Orange';
    if (solutionColor === INDICATOR_COLORS.endpoint) return 'Red (Endpoint!)';
    return 'Unknown';
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part IV: Titration</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Prepare', hint: 'Place flask on white tile, prepare to swirl gently', done: stirrerOn },
            { id: 2, title: 'Begin Titration', hint: 'Start adding HCl', done: titrationStarted },
            { id: 3, title: 'Approach Endpoint', hint: 'Watch for color change', done: approachingEndpoint },
            { id: 4, title: 'Detect Endpoint', hint: 'Pink colour persists', done: endpointReached },
            { id: 5, title: 'Record Final', hint: 'Note burette reading', done: finalRecorded }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Results Display */}
        {finalRecorded && (
          <div className="results-panel">
            <h4>Titration Results</h4>
            <div className="result-row">
              <span>Initial Reading:</span>
              <span className="value">{initialReading.toFixed(2)} mL</span>
            </div>
            <div className="result-row">
              <span>Final Reading:</span>
              <span className="value">{finalReading.toFixed(2)} mL</span>
            </div>
            <div className="result-row highlight">
              <span>Volume HCl Used:</span>
              <span className="value">{volumeUsed.toFixed(2)} mL</span>
            </div>
          </div>
        )}

        {/* Color Reference */}
        <div className="color-reference">
          <h4>Color Transitions</h4>
          <div className="color-scale">
            {[
              { color: INDICATOR_COLORS.basic, label: 'Start' },
              { color: INDICATOR_COLORS.transition1, label: '' },
              { color: INDICATOR_COLORS.transition2, label: '' },
              { color: INDICATOR_COLORS.transition3, label: '' },
              { color: INDICATOR_COLORS.endpoint, label: 'End' }
            ].map((item, idx) => (
              <div key={idx} className="color-step">
                <div
                  className={`color-swatch ${solutionColor === item.color ? 'active' : ''}`}
                  style={{ background: item.color }}
                />
                {item.label && <span>{item.label}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <LabBench
        experimentName="Antacid Titration"
        completedCount={completedSteps}
        totalSteps={5}
        equipment={['Burette', 'Erlenmeyer flask', 'White tile or paper']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="titration-workspace">
          <div className="titration-setup">
            {/* Burette */}
            <div className="burette-section">
              <Burette
                volume={50 - buretteReading}
                maxVolume={50}
                liquidColor="rgba(200, 255, 200, 0.6)"
                stopcock={stopcockOpen ? 'open' : 'closed'}
                onStopcock={titrationStarted && !endpointReached ? handleToggleStopcock : undefined}
              />
              <div className="burette-reading">
                Reading: {buretteReading.toFixed(2)} mL
              </div>
            </div>

            {/* Flask on Stirrer */}
            <div className="flask-stirrer-combo">
              <div
                className={`flask-section ${!stirrerOn ? 'clickable' : ''}`}
                onClick={!stirrerOn ? handleStartStirrer : undefined}
              >
                <ErlenmeyerFlask
                  size={250}
                  fillLevel={60}
                  fillColor={solutionColor}
                  hasStirBar={false}
                  stirring={false}
                  active={currentStep === 1 && !stirrerOn}
                />
              </div>
            </div>
          </div>

          {/* Current Color Display */}
          <div className="color-display">
            <div className="current-color" style={{ background: solutionColor }}>
              <span style={{ color: solutionColor === INDICATOR_COLORS.basic ? '#333' : '#FFF' }}>
                {getColorName()}
              </span>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {!stirrerOn && (
              <div className="status-message info">
                <Droplets size={20} />
                Click the flask to place on white tile and prepare to swirl gently
              </div>
            )}
            {stirrerOn && !titrationStarted && (
              <div className="status-message info" onClick={handleBeginTitration} style={{ cursor: 'pointer' }}>
                <Droplets size={20} />
                Click here to begin the titration
              </div>
            )}
            {titrationStarted && !approachingEndpoint && !endpointReached && (
              <div className="status-message warning">
                <AlertCircle size={20} />
                Click stopcock to {stopcockOpen ? 'close' : 'open'} - add HCl steadily
              </div>
            )}
            {approachingEndpoint && !endpointReached && (
              <div className="status-message warning">
                <AlertCircle size={20} />
                APPROACHING ENDPOINT! Add HCl dropwise. Click stopcock to control flow.
              </div>
            )}
            {endpointReached && !finalRecorded && (
              <div className="status-message success" onClick={handleRecordFinal} style={{ cursor: 'pointer' }}>
                <Check size={20} />
                ENDPOINT REACHED! Click here to record the final reading.
              </div>
            )}
            {finalRecorded && (
              <div className="status-message success">
                <Check size={20} />
                Titration complete! Volume of HCl used: {volumeUsed.toFixed(2)} mL
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Endpoint Detection</h4>
            <p>
              The endpoint is reached when the methyl orange indicator turns from yellow
              through orange to a persistent pink colour that doesn&apos;t fade with swirling.
            </p>
            <p>
              Near the endpoint, add HCl dropwise and swirl gently after each addition.
              Titrate until a pink colour persists.
            </p>
          </div>
        </div>

        <style>{`
          .titration-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .titration-setup {
            display: flex;
            gap: 60px;
            align-items: flex-end;
            justify-content: center;
          }

          .burette-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .burette-reading {
            background: #1a1a1a;
            padding: 6px 12px;
            border-radius: 4px;
            font-family: monospace;
            font-size: 14px;
            color: #00ff88;
          }

          .flask-stirrer-combo {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0;
          }

          .flask-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s;
          }

          .flask-section.clickable {
            cursor: pointer;
          }

          .flask-section.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .color-display {
            margin-top: 10px;
          }

          .current-color {
            padding: 12px 24px;
            border-radius: 8px;
            min-width: 180px;
            text-align: center;
            border: 2px solid rgba(0,0,0,0.2);
            transition: background-color 0.3s ease;
          }

          .current-color span {
            font-weight: 600;
            font-size: 14px;
          }

          .color-reference {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 16px;
          }

          .color-reference h4 {
            margin: 0 0 10px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .color-scale {
            display: flex;
            gap: 4px;
            align-items: center;
          }

          .color-step {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
          }

          .color-swatch {
            width: 24px;
            height: 24px;
            border-radius: 4px;
            border: 2px solid transparent;
            transition: all 0.2s;
          }

          .color-swatch.active {
            border-color: white;
            transform: scale(1.2);
            box-shadow: 0 0 10px rgba(255,255,255,0.5);
          }

          .color-step span {
            font-size: 9px;
            color: var(--text-secondary);
          }

          .results-panel {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            margin-top: 16px;
          }

          .results-panel h4 {
            margin: 0 0 12px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .result-row {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            font-size: 13px;
          }

          .result-row .value {
            font-family: monospace;
            font-weight: 600;
          }

          .result-row.highlight {
            background: rgba(0, 168, 107, 0.1);
            padding: 8px;
            border-radius: 4px;
            margin-top: 8px;
          }

          .result-row.highlight .value {
            color: var(--secondary);
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.warning {
            background: rgba(251, 191, 36, 0.2);
            color: #FBBF24;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box p {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .theory-box p:last-child {
            margin-bottom: 0;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp10"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
